import throttle from 'lodash/throttle';
import { useCallback, useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';

import { logWidgetScrolledToBottom } from '../../bi/loggers/logWidgetScrolledToBottom';
import { biEngagementSlice } from '../reducers/biEngagement';

type BiEngagementEventHandlers = {
  onScroll: (event: Event) => void;
  onMouseClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseMove: (event: React.MouseEvent<HTMLDivElement>) => void;
  onScrollAddedContent: () => void;
};

const THROTTLE_TIME = 1000;

export const useBiEngagementEventHandlers = (): BiEngagementEventHandlers => {
  const dispatch = useDispatch();
  const store = useStore();

  const logScrollToBottom = useMemo(() => {
    return throttle(() => {
      logWidgetScrolledToBottom(store.getState());
      dispatch(biEngagementSlice.actions.setScrollToBottom(true));
    }, THROTTLE_TIME);
  }, [dispatch, store]);

  const logScroll = useMemo(() => {
    return throttle(() => dispatch(biEngagementSlice.actions.setScroll(true)), THROTTLE_TIME);
  }, [dispatch]);

  const logMouseClick = useMemo(() => {
    return throttle(() => dispatch(biEngagementSlice.actions.setMouseClick(true)), THROTTLE_TIME);
  }, [dispatch]);

  const logMouseMove = useMemo(() => {
    return throttle(() => dispatch(biEngagementSlice.actions.setMouseMove(true)), THROTTLE_TIME);
  }, [dispatch]);

  const logScrollAddedContent = useMemo(() => {
    return throttle(() => dispatch(biEngagementSlice.actions.setScrollAddedContent(true)), THROTTLE_TIME);
  }, [dispatch]);

  const logScrollPosition = useMemo(() => {
    return throttle((maxScrollAtFirstPosition: boolean, scrollPercentage: number) => {
      dispatch(biEngagementSlice.actions.setMaxScrollAtFirstPosition(maxScrollAtFirstPosition));
      dispatch(biEngagementSlice.actions.setScrollPercentage(scrollPercentage));
    }, THROTTLE_TIME);
  }, [dispatch]);

  const onScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, offsetHeight, clientHeight } = e.target;
      const maxScrollAtFirstPosition = clientHeight === scrollHeight;
      const scrollPercentage = maxScrollAtFirstPosition
        ? 100
        : Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);

      logScrollPosition(maxScrollAtFirstPosition, scrollPercentage);

      if (offsetHeight + scrollTop >= scrollHeight) {
        logScrollToBottom();
      } else {
        logScroll();
      }
    },
    [logScrollToBottom, logScroll],
  );

  return {
    onScroll,
    onMouseClick: logMouseClick,
    onMouseMove: logMouseMove,
    onScrollAddedContent: logScrollAddedContent,
  };
};
